class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // Handle overlay click to close drawer
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }
    
    // Also handle clicks on the main drawer container (but not on the drawer inner)
    this.addEventListener('click', (e) => {
      // If click is on the container but not on the drawer inner, close it
      if (e.target === this || e.target.classList.contains('oo-cart-drawer__overlay')) {
        this.close();
      }
    });
    
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Initialize Swiper slider after drawer opens
        this.initSwiperSlider();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    
    // Also try to initialize Swiper immediately (in case transitionend doesn't fire)
    setTimeout(() => {
      this.initSwiperSlider();
    }, 500);
  }
  
  initSwiperSlider() {
    // Try multiple times with increasing delays to ensure slider is in DOM
    [200, 400, 600, 1000, 1500].forEach((delay) => {
      setTimeout(() => {
        const slider = this.querySelector('.cart-drawer-recommendations-slider');
        if (slider) {
          const wrapper = slider.querySelector('.swiper-wrapper');
          if (wrapper && wrapper.children.length > 0) {
            // If Swiper is already initialized, don't reinitialize
            if (slider.swiper) {
              return;
            }
            
            // Try to initialize via global function
            if (typeof window.initCartDrawerSwiper === 'function') {
              window.initCartDrawerSwiper();
              return;
            }
            
            // Fallback: direct initialization if Swiper library is available
            if (typeof Swiper !== 'undefined') {
              try {
                const containerWidth = slider.offsetWidth;
                const fixedSlideWidth = (containerWidth / 1.5) - 16;
                
                const swiperInstance = new Swiper(slider, {
                  loop: false,
                  autoplay: false,
                  slidesPerView: 'auto',
                  spaceBetween: 16,
                  freeMode: true,
                  watchSlidesProgress: false,
                  observer: false,
                  observeParents: false,
                  resizeObserver: false,
                  updateOnWindowResize: false
                });
                
                // Fix slide widths
                setTimeout(() => {
                  const slides = slider.querySelectorAll('.swiper-slide');
                  slides.forEach((slide) => {
                    slide.style.width = fixedSlideWidth + 'px';
                    slide.style.maxWidth = fixedSlideWidth + 'px';
                    slide.style.minWidth = fixedSlideWidth + 'px';
                    slide.style.flexShrink = '0';
                  });
                }, 100);
              } catch (e) {
                console.error('Error initializing Swiper:', e);
              }
            }
          }
        }
      }, delay);
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      // Skip cart-icon-bubble section update to prevent button from disappearing
      if (section.id === 'cart-icon-bubble') {
        return;
      }
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
        const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        sectionElement.innerHTML = newHTML;
        
        // Re-execute scripts in the new HTML (scripts don't run automatically with innerHTML)
        sectionElement.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attr) => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
      }
    });

    // Update cart count in header bubble
    if (parsedState.item_count !== undefined) {
      const cartBubble = document.querySelector('.cart-bubble');
      if (cartBubble) {
        cartBubble.textContent = parsedState.item_count;
        cartBubble.style.display = parsedState.item_count > 0 ? 'flex' : 'none';
      }
      // Also ensure the cart button itself is visible
      const cartButton = document.querySelector('#cart-icon-bubble');
      if (cartButton) {
        cartButton.style.display = '';
      }
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        // Remove any existing listeners and add new one
        const newOverlay = overlay.cloneNode(true);
        overlay.parentNode.replaceChild(newOverlay, overlay);
        newOverlay.addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
        });
      }
      this.open();
      
      // Dispatch custom event to re-initialize handlers
      setTimeout(() => {
        this.dispatchEvent(new CustomEvent('cart-drawer-updated', { bubbles: true }));
        
        // Initialize Swiper slider after content is rendered
        this.initSwiperSlider();
      }, 100);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return [
        {
          id: 'CartDrawer-CartItems',
          section: 'cart-drawer',
          selector: '.js-contents',
        },
        {
          id: 'CartDrawer-Footer',
          section: 'cart-drawer',
          selector: '.oo-cart-drawer__footer',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    super.updateQuantity(line, quantity, name, variantId);
    
    // After cart update, ensure cart button remains visible
    setTimeout(() => {
      const cartButton = document.querySelector('#cart-icon-bubble');
      if (cartButton) {
        cartButton.style.display = '';
      }
    }, 100);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
